import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "protobuf-decoder-lang";

export function detectBrowserLanguage() {
  if (typeof navigator === "undefined") return "en";
  const raw =
    (Array.isArray(navigator.languages) && navigator.languages[0]) ||
    navigator.language ||
    "en";
  const lower = String(raw).toLowerCase();
  if (lower.startsWith("zh")) return "zh";
  return "en";
}

const DICT = {
  en: {
    // App header + toggles
    app_title: "Protobuf Decoder",
    app_subtitle:
      "Tool to decode Protobuf without having the original .proto files. All decoding is done locally via JavaScript.",
    hide_docs: "Hide Docs",
    show_docs: "Show Docs",
    hide_input_formats: "Hide Input Formats",
    show_input_formats: "Show Input Formats",
    upload_file: "Upload File",
    language: "Language",
    lang_en: "English",
    lang_zh: "中文",
    help: "Help",

    // Docs block
    official_documentation: "Official Documentation",
    official_documentation_desc:
      "For more information about Protocol Buffers, please refer to the official documentation:",
    proto3_language_guide: "Proto3 Language Guide",
    protocol_buffer_encoding: "Protocol Buffer Encoding",
    official_syntax_spec: "Official syntax and language specification",
    official_encoding_spec: "Official encoding format documentation",

    // Input / normalize
    input_data_source: "Input Data (Source)",
    input_placeholder:
      "Paste your protobuf data here... (supports hex, base64, Java decimal arrays, and Java escape strings)",
    detected_format: "Detected Format: {format}",
    normalizing: "Normalizing...",
    normalized_hex_readonly: "Normalized Hex (Read-only)",
    normalized_hex_placeholder:
      "(auto) Parsed bytes will appear here as pretty hex...",
    copy: "Copy",
    clear: "Clear",
    decode: "Decode",
    parse_delimited: "parse varint length delimited input",
    input_error_title: "Input Error",
    input_error_empty: "Please paste input data first.",
    input_error_parse: "Failed to parse input.",
    copied: "{label} copied",
    copy_failed: "Copy failed",

    // Formats
    format_java_decimal_array: "Java Decimal Array",
    format_java_escape_string: "Java Escape String",
    format_hex: "Hexadecimal",
    format_base64: "Base64",
    format_file: "File Upload",
    format_unknown: "Unknown",

    // Result + advanced
    using_smart_output: "Using Smart Decode Output",
    revert_to_source: "Revert to Source Input",
    result: "Result",
    performance: "Performance",
    parse_time: "Parse time: {ms}ms",
    no_decoded_data: "No Decoded Data",
    click_decode_hint:
      "Click Decode after providing input to see the parsed structure.",
    advanced: "Advanced",
    schema_based_display: "Schema-Based Display",
    smart_decode: "Smart Decode",
    protobuf_message_schema: "Protobuf Message Schema",
    schema_placeholder: "Enter protobuf message schema...",
    select_message: "Select Message",
    select_message_placeholder: "Select a message from schema",
    no_matching_message: "No matching message",
    schema_validation_error: "Schema Validation Error",
    supported_input_formats: "Supported Input Formats",

    // ProtobufDisplay
    protobuf_decode_result: "Protobuf Decode Result",
    protobuf_total: "Total: {bytes} bytes | Messages: {messages}",
    protobuf_message: "Message {index} ({bytes} bytes)",
    copy_source: "Copy Source",
    copied_message_source: "Message source data copied to clipboard!",
    left_over_bytes: "Left over bytes: {hex}",

    // SchemaBasedDisplay
    schema_no_schema: "No Schema",
    schema_no_schema_help:
      "Please input a protobuf message schema to enable schema-based display.",
    schema_no_messages_found: "No Messages Found",
    schema_no_valid_messages: "No valid message definitions found in the schema.",
    schema_select_message: "Select Message",
    schema_select_message_help:
      "Please select a message from the schema to display the decoded data.",
    schema_available_messages: "Available messages: {messages}",
    schema_fields_defined: "{count} fields defined",
    schema_fields_found: "{count} fields found",
    table_field_number: "Field #",
    table_field_name: "Field Name",
    table_type: "Type",
    table_wire_type: "Wire Type",
    table_value: "Value",

    // SmartDecoder
    smart_decoder: "Smart Decoder",
    smart_subtitle: "XOR decrypt bytes data based on field conditions or manual input",
    smart_conditional_xor: "Conditional XOR",
    smart_manual_xor: "Manual XOR",
    smart_enable: "Enable Smart Decode",
    enabled: "Enabled",
    disabled: "Disabled",
    smart_condition_field: "Condition Field",
    smart_condition_value: "Condition Value",
    smart_target_field: "Target Field (to decrypt)",
    smart_xor_field: "XOR Field (key)",
    smart_run: "Perform Smart Decode",
    smart_result: "Smart Decode Result",
    smart_use_as_input: "Use decrypted bytes as Active Decode Input",
    smart_input_data: "Input Data",
    smart_xor_value: "XOR Value",
    smart_calculate: "Calculate XOR",
  },
  zh: {
    app_title: "Protobuf 解码器",
    app_subtitle:
      "无需原始 .proto 文件即可解码 Protobuf。所有解码都在本地浏览器中通过 JavaScript 完成。",
    hide_docs: "收起文档",
    show_docs: "展开文档",
    hide_input_formats: "收起输入格式",
    show_input_formats: "展开输入格式",
    upload_file: "上传文件",
    language: "语言",
    lang_en: "English",
    lang_zh: "中文",
    help: "帮助",

    official_documentation: "官方文档",
    official_documentation_desc: "更多信息请参考官方文档：",
    proto3_language_guide: "Proto3 语言指南",
    protocol_buffer_encoding: "Protocol Buffer 编码格式",
    official_syntax_spec: "官方语法与语言规范",
    official_encoding_spec: "官方编码格式文档",

    input_data_source: "输入数据（源）",
    input_placeholder:
      "在此粘贴 protobuf 数据...（支持 hex、base64、Java 十进制数组、Java 转义字符串）",
    detected_format: "检测到的格式：{format}",
    normalizing: "正在规范化…",
    normalized_hex_readonly: "规范化 Hex（只读）",
    normalized_hex_placeholder: "（自动）解析后的字节会以易读 hex 显示在这里…",
    copy: "复制",
    clear: "清空",
    decode: "解码",
    parse_delimited: "解析 varint 长度分隔输入",
    input_error_title: "输入错误",
    input_error_empty: "请先粘贴输入数据。",
    input_error_parse: "输入解析失败。",
    copied: "已复制：{label}",
    copy_failed: "复制失败",

    format_java_decimal_array: "Java 十进制数组",
    format_java_escape_string: "Java 转义字符串",
    format_hex: "十六进制",
    format_base64: "Base64",
    format_file: "文件上传",
    format_unknown: "未知",

    using_smart_output: "当前使用智能解码输出",
    revert_to_source: "回退到源输入",
    result: "结果",
    performance: "性能",
    parse_time: "解析耗时：{ms}ms",
    no_decoded_data: "暂无解码结果",
    click_decode_hint: "提供输入后点击“解码”查看解析结构。",
    advanced: "高级功能",
    schema_based_display: "基于 Schema 展示",
    smart_decode: "智能解码",
    protobuf_message_schema: "Protobuf 消息 Schema",
    schema_placeholder: "在此输入 protobuf message schema…",
    select_message: "选择 Message",
    select_message_placeholder: "从 schema 中选择一个 message",
    no_matching_message: "没有匹配的 message",
    schema_validation_error: "Schema 校验错误",
    supported_input_formats: "支持的输入格式",

    protobuf_decode_result: "Protobuf 解码结果",
    protobuf_total: "总计：{bytes} 字节 | 消息数：{messages}",
    protobuf_message: "消息 {index}（{bytes} 字节）",
    copy_source: "复制源数据",
    copied_message_source: "已复制该消息源数据到剪贴板！",
    left_over_bytes: "剩余字节：{hex}",

    schema_no_schema: "未提供 Schema",
    schema_no_schema_help: "请输入 protobuf message schema 以启用 schema 展示。",
    schema_no_messages_found: "未找到 Message",
    schema_no_valid_messages: "在 schema 中未找到有效的 message 定义。",
    schema_select_message: "请选择 Message",
    schema_select_message_help: "请选择一个 message 来展示解码数据。",
    schema_available_messages: "可用 messages：{messages}",
    schema_fields_defined: "Schema 定义字段：{count}",
    schema_fields_found: "解析到字段：{count}",
    table_field_number: "字段号",
    table_field_name: "字段名",
    table_type: "类型",
    table_wire_type: "Wire 类型",
    table_value: "值",

    // SmartDecoder
    smart_decoder: "智能解码",
    smart_subtitle: "基于字段条件或手动输入对 bytes 进行 XOR 解密",
    smart_conditional_xor: "条件 XOR",
    smart_manual_xor: "手动 XOR",
    smart_enable: "启用智能解码",
    enabled: "已启用",
    disabled: "未启用",
    smart_condition_field: "条件字段",
    smart_condition_value: "条件值",
    smart_target_field: "目标字段（待解密）",
    smart_xor_field: "XOR 字段（密钥）",
    smart_run: "执行智能解码",
    smart_result: "智能解码结果",
    smart_use_as_input: "将解密结果作为主解码输入",
    smart_input_data: "输入数据",
    smart_xor_value: "XOR 值",
    smart_calculate: "计算 XOR",
  },
};

function formatTemplate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`
  );
}

function createTranslator(lang) {
  const safeLang = DICT[lang] ? lang : "en";
  return function t(key, vars) {
    const dict = DICT[safeLang] || DICT.en;
    const fallback = DICT.en || {};
    const template = dict[key] ?? fallback[key] ?? key;
    return formatTemplate(template, vars);
  };
}

export function useI18n() {
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    if (typeof window === "undefined") {
      setLangState(detectBrowserLanguage());
      return;
    }
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && DICT[stored]) {
        setLangState(stored);
      } else {
        setLangState(detectBrowserLanguage());
      }
    } catch {
      setLangState(detectBrowserLanguage());
    }
  }, []);

  const setLang = useCallback((next) => {
    const safe = DICT[next] ? next : "en";
    setLangState(safe);
    try {
      window.localStorage.setItem(STORAGE_KEY, safe);
    } catch {
      // ignore
    }
  }, []);

  const t = useMemo(() => createTranslator(lang), [lang]);
  return { t, lang, setLang };
}

// Backward compatible wrapper: app entry may wrap <App /> with <I18nProvider>.
// Current implementation does not require context; this provider is a no-op.
export function I18nProvider({ children }) {
  return children;
}
